import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText } from 'lucide-react';

import { previewKindFromResponse } from './previewable';

export interface PreviewState {
  readonly status: 'loading' | 'ready' | 'error';
  readonly objectUrl: string | null;
  readonly contentType: string | null;
}

interface AttachmentPreviewBodyProps {
  readonly state: PreviewState;
  readonly fileName: string | null;
  readonly title: string;
}

export function AttachmentPreviewBody({
  state,
  fileName,
  title,
}: AttachmentPreviewBodyProps): ReactElement {
  const { t } = useTranslation();

  if (state.status === 'loading') {
    return (
      <PreviewMessage tone="muted">
        {t('patient.documents.previewLoading')}
      </PreviewMessage>
    );
  }

  if (state.status === 'error') {
    return (
      <PreviewMessage tone="destructive">
        {t('patient.documents.previewError')}
      </PreviewMessage>
    );
  }

  const kind = previewKindFromResponse(state.contentType, fileName);
  const objectUrl = state.objectUrl ?? '';

  if (kind === 'pdf') {
    return (
      <iframe
        // `#navpanes=0` opens the browser PDF viewer with its thumbnail/bookmark
        // side pane collapsed (more room for the page in this modal); the
        // toolbar stays so the user can still page/zoom/download.
        src={objectUrl.length > 0 ? `${objectUrl}#navpanes=0` : objectUrl}
        title={title}
        className="w-full h-[70vh] border border-border rounded-md bg-muted/20"
      />
    );
  }

  if (kind === 'image') {
    return (
      <div className="bg-muted/30 border border-border rounded-md flex items-center justify-center max-h-[70vh] overflow-hidden">
        <img
          src={objectUrl}
          alt={title}
          className="max-w-full max-h-[70vh] object-contain"
        />
      </div>
    );
  }

  return (
    <PreviewMessage tone="muted">
      {t('patient.documents.previewUnsupported')}
    </PreviewMessage>
  );
}

function PreviewMessage({
  tone,
  children,
}: {
  readonly tone: 'muted' | 'destructive';
  readonly children: string;
}): ReactElement {
  const iconTone = tone === 'destructive' ? 'text-destructive' : 'text-muted-foreground';
  return (
    <div className="bg-muted/40 border border-border rounded-md p-8 flex flex-col items-center justify-center text-center min-h-[200px]">
      <FileText className={`h-10 w-10 mb-3 ${iconTone}`} aria-hidden="true" />
      <p className="text-sm text-muted-foreground">{children}</p>
    </div>
  );
}
